import ChatAttachmentsService from '@/services/ChatAttachmentsService.js';
import { isStickerFileName } from '@/modules/pbxCloud/components/Chat/emoji-picker/stickers.js';

// FEX-1192: los stickers viajan como imágenes adjuntas, pero no son archivos
// que la gente haya compartido: fuera del panel de archivos. El backend no los
// distingue, así que se filtran aquí, en los dos únicos puntos de entrada.
function withoutStickers(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.filter(
    (att) => !isStickerFileName(att.file_name || att.file_url || att.attached || att.url)
  );
}

const chatAttachments = {
  namespaced: true,
  state: {
    showAttachmentsPanel: false,
    attachments: [],
    // Stickers ocultados de las páginas ya cargadas (FEX-1192).
    discardedStickers: 0,

    currentPage: 1,
    pageSize: 20,
    totalAttachments: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    isLoading: false,
    isLoadingMore: false,
    searchQuery: '',
    filterType: 'ALL',
    selectedChatId: null,
    viewMode: 'grid',
  },

  mutations: {
    SET_SHOW_ATTACHMENTS_PANEL(state, show) {
      state.showAttachmentsPanel = show;
    },

    SET_ATTACHMENTS(state, attachments) {
      const visible = withoutStickers(attachments);
      // El total lo pagina el backend e incluye stickers; descontamos los que
      // ocultamos de esta página para que el contador no mienta de más.
      state.discardedStickers = (attachments || []).length - visible.length;
      state.attachments = visible;
    },

    APPEND_ATTACHMENTS(state, attachments) {
      const visible = withoutStickers(attachments);
      state.discardedStickers += (attachments || []).length - visible.length;
      state.attachments = [...state.attachments, ...visible];
    },

    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page;
    },

    SET_TOTAL_ATTACHMENTS(state, total) {
      state.totalAttachments = Math.max(0, total - state.discardedStickers);
    },

    SET_HAS_NEXT_PAGE(state, hasNext) {
      state.hasNextPage = hasNext;
    },

    SET_HAS_PREVIOUS_PAGE(state, hasPrevious) {
      state.hasPreviousPage = hasPrevious;
    },

    SET_IS_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_IS_LOADING_MORE(state, isLoadingMore) {
      state.isLoadingMore = isLoadingMore;
    },

    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query;
    },

    SET_FILTER_TYPE(state, type) {
      state.filterType = type;
    },


    SET_SELECTED_CHAT_ID(state, chatId) {
      state.selectedChatId = chatId;
    },

    SET_VIEW_MODE(state, mode) {
      state.viewMode = mode;
    },

    RESET_ATTACHMENTS_STATE(state) {
      state.attachments = [];
      state.discardedStickers = 0;
      state.currentPage = 1;
      state.totalAttachments = 0;
      state.hasNextPage = false;
      state.hasPreviousPage = false;
      state.searchQuery = '';
      state.filterType = 'ALL';
      state.isLoading = false;
      state.isLoadingMore = false;
    },
  },

  actions: {

    openAttachmentsPanel({ commit, dispatch, rootState }, chatId) {
      if (rootState.chat && rootState.chat.searchInChatActive) {
        commit('chat/SET_SEARCH_IN_CHAT_ACTIVE', false, { root: true });
        commit('chat/CLEAR_SEARCH_IN_CHAT', null, { root: true });
      }
      commit('SET_SELECTED_CHAT_ID', chatId);
      commit('SET_SHOW_ATTACHMENTS_PANEL', true);
      commit('RESET_ATTACHMENTS_STATE');
      dispatch('fetchAttachments');
    },

    closeAttachmentsPanel({ commit }) {
      commit('SET_SHOW_ATTACHMENTS_PANEL', false);
      commit('RESET_ATTACHMENTS_STATE');
    },

    async fetchAttachments({ state, commit }, resetList = true) {
      if (!state.selectedChatId) {
        console.error('No chat selected');
        return;
      }

      if (resetList) {
        commit('SET_IS_LOADING', true);
        commit('SET_CURRENT_PAGE', 1);
      } else {
        commit('SET_IS_LOADING_MORE', true);
      }

      try {
        const params = {
          page: state.currentPage,
          page_size: state.pageSize,
          file_type: state.filterType,
          search: state.searchQuery,
        };

        const response = await ChatAttachmentsService.getAttachments(
          state.selectedChatId,
          params
        );

        const { attachments, pagination } = response.data;

        if (resetList) {
          commit('SET_ATTACHMENTS', attachments);
        } else {
          commit('APPEND_ATTACHMENTS', attachments);
        }

        commit('SET_TOTAL_ATTACHMENTS', pagination.total_items);
        commit('SET_HAS_NEXT_PAGE', pagination.current_page < pagination.total_pages);
        commit('SET_HAS_PREVIOUS_PAGE', pagination.current_page > 1);

        return 'Success';
      } catch (error) {
        console.error('Error fetching attachments:', error);
        commit('SET_ATTACHMENTS', []);
        commit('SET_TOTAL_ATTACHMENTS', 0);
        return 'Error';
      } finally {
        commit('SET_IS_LOADING', false);
        commit('SET_IS_LOADING_MORE', false);
      }
    },


    async loadMoreAttachments({ state, commit, dispatch }) {
      if (!state.hasNextPage || state.isLoadingMore) {
        return;
      }

      commit('SET_CURRENT_PAGE', state.currentPage + 1);
      await dispatch('fetchAttachments', false);
    },

    async updateSearchQuery({ commit, dispatch }, query) {
      commit('SET_SEARCH_QUERY', query);
      commit('SET_CURRENT_PAGE', 1);
      await dispatch('fetchAttachments', true);
    },

    async updateFilterType({ commit, dispatch }, type) {
      commit('SET_FILTER_TYPE', type);
      commit('SET_CURRENT_PAGE', 1);
      await dispatch('fetchAttachments', true);
    },

    setViewMode({ commit }, mode) {
      commit('SET_VIEW_MODE', mode);
    },

    async scrollToMessage({ rootState }, messageId) {
      const message = rootState.conversation.messages
        .flatMap(day => day.data)
        .find(msg => msg.id === messageId);

      if (message) {

        setTimeout(() => {
          const messageElement = document.getElementById(messageId);
          if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            messageElement.classList.add('flash');
            setTimeout(() => {
              messageElement.classList.remove('flash');
            }, 2000);
          }
        }, 100);
      } else {
        console.log('Message not loaded yet, need to fetch from:', messageId);
      }
    },
  },

  getters: {
    hasAttachments: (state) => state.attachments.length > 0,
    totalPages: (state) => Math.ceil(state.totalAttachments / state.pageSize),
    isFirstPage: (state) => state.currentPage === 1,
    isLastPage: (state) => !state.hasNextPage,
    attachmentsByType: (state) => (type) => {
      if (type === 'ALL') return state.attachments;
      return state.attachments.filter(att => att.file_type === type);
    },
  },
};

export default chatAttachments;
